import { FC, ReactNode, useState } from "react";
import { Dropdown, Form } from "react-bootstrap";
import { ArrowDown, ArrowUp, Files, LightningCharge, PlusLg, Trash3 } from "react-bootstrap-icons";
import { Question, Choice, ResponseAction, Priority, Difficulty } from "../protos/backendproto/QuestionService_pb";

const PRIORITY_OPTIONS = [
    { value: Priority.PRIORITY_SAFETY, label: "Safety", tone: "danger" },
    { value: Priority.PRIORITY_FUNCTIONAL, label: "Functional", tone: "warn" },
    { value: Priority.PRIORITY_COSMETIC, label: "Cosmetic", tone: "info" },
];

const DIFFICULTY_OPTIONS = [
    { value: Difficulty.DIFFICULTY_NO_EXPERIENCE, label: "No experience" },
    { value: Difficulty.DIFFICULTY_HANDYMAN, label: "Handyman" },
    { value: Difficulty.DIFFICULTY_PRO, label: "Licensed tradesman" },
    { value: Difficulty.DIFFICULTY_CONTRACTOR, label: "Contractor" },
];

const Section: FC<{ title: string, action?: ReactNode, children: ReactNode }> = ({ title, action, children }) => (
    <section className="panel editor-section">
        <div className="section-title">
            {title}
            {action && <span className="ms-auto">{action}</span>}
        </div>
        {children}
    </section>
)

// The editor is remounted (keyed by the parent) whenever the selected question
// changes or edits are discarded, so local state only needs its initial value.
const QuestionEditorPane: FC<{
    hasEditAccess: Boolean,
    question: Question,
    allQuestions: Question[],
    groupName: string,
    position: { index: number, count: number },
    copyTargets: string[],
    needsSave: Boolean,
    isSaving: Boolean,
    updateQuestion: ((question: Question) => void),
    copyQuestionTo: ((groupIdx: number) => void),
    moveQuestionIdxUp: (() => void),
    moveQuestionIdxDown: (() => void),
    saveChanges: (() => void),
    discardChanges: (() => void)
}> = (({ hasEditAccess, question, allQuestions, groupName, position, copyTargets, needsSave, isSaving, updateQuestion,
    copyQuestionTo, moveQuestionIdxUp, moveQuestionIdxDown, saveChanges, discardChanges }) => {
    const [isConditionalQuestion, setIsConditionalQuestion] = useState(question.hasDependentQuestionId());
    const disabled = !hasEditAccess;

    // Every edit works on a copy so the list's own Question objects are only
    // replaced when the change is saved.
    function edit(mutation: (q: Question) => void) {
        const edited = Question.deserializeBinary(question.serializeBinary());
        mutation(edited);
        updateQuestion(edited);
    }

    function editAction(idx: number, mutation: (a: ResponseAction) => void) {
        edit((q) => mutation(q.getActionsList()[idx]));
    }

    const choicesList = question.getChoicesList();
    const actionsList = question.getActionsList();

    let allDependentQuestionChoices: Choice[] = []
    let dependentQuestionMissing = false
    if (question.hasDependentQuestionId()) {
        let questionWithMatchingId = allQuestions.filter((q) => {
            return q.getId() === question.getDependentQuestionId()
        })
        if (questionWithMatchingId.length === 1) {
            allDependentQuestionChoices = questionWithMatchingId[0].getChoicesList()
        } else {
            dependentQuestionMissing = true
        }
    }

    let selectedDependentChoicesIdSet: Set<string> = new Set(question.getDependentQuestionChoiceIdsList())

    return <div className="editor">
        <div className="editor-header">
            <div className="min-w-0 flex-grow-1">
                <div className="editor-crumb">
                    {groupName || "Untitled"} <span className="mx-1">›</span> Question {position.index + 1} of {position.count}
                </div>
                <div className="editor-title text-truncate">{question.getPrompt() || "Untitled question"}</div>
            </div>
            {hasEditAccess && <div className="d-flex align-items-center gap-1">
                <button className="icon-btn" title="Move up" disabled={position.index === 0} onClick={moveQuestionIdxUp}><ArrowUp /></button>
                <button className="icon-btn" title="Move down" disabled={position.index === position.count - 1} onClick={moveQuestionIdxDown}><ArrowDown /></button>
                <Dropdown align="end">
                    <Dropdown.Toggle as="button" className="icon-btn no-caret" title="Copy to…"><Files /></Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Header>Copy question to</Dropdown.Header>
                        {copyTargets.map((name, idx) => {
                            return <Dropdown.Item key={"copy-to-" + idx} onClick={() => copyQuestionTo(idx)}>{name || "Untitled"}</Dropdown.Item>
                        })}
                    </Dropdown.Menu>
                </Dropdown>
            </div>}
            {hasEditAccess && needsSave && <div className="d-flex gap-2 save-actions">
                <button className="btn btn-ghost" onClick={discardChanges}>Discard</button>
                <button className="btn btn-accent" disabled={!!isSaving} onClick={saveChanges}>
                    Save question <span className="kbd kbd-on-accent">⌘S</span>
                </button>
            </div>}
        </div>

        <div className="editor-body stagger">
            <Section title="Question">
                <div className="row g-3">
                    <div className="col-12">
                        <label className="field-label" htmlFor="q-prompt">Prompt</label>
                        <Form.Control id="q-prompt" disabled={disabled} type="text" placeholder="What would you like to ask?" value={question.getPrompt()}
                            onChange={(e) => edit((q) => q.setPrompt(e.target.value))} />
                    </div>
                    <div className="col-12">
                        <label className="field-label" htmlFor="q-hint">Hint</label>
                        <Form.Control id="q-hint" disabled={disabled} as="textarea" rows={2} placeholder="Optional guidance shown under the question" value={question.getHint()}
                            onChange={(e) => edit((q) => q.setHint(e.target.value))} />
                    </div>
                    <div className="col-md-6">
                        <label className="field-label" htmlFor="q-id">ID</label>
                        <Form.Control id="q-id" className="mono" disabled={disabled} type="text" value={question.getId()}
                            onChange={(e) => edit((q) => q.setId(e.target.value))} />
                    </div>
                </div>
            </Section>

            <Section title={`Choices · ${choicesList.length}`} action={hasEditAccess &&
                <button className="btn btn-ghost btn-sm" onClick={() => edit((q) => q.setChoicesList([...q.getChoicesList(), new Choice()]))}>
                    <PlusLg /> Add choice
                </button>}>
                {choicesList.length === 0 && <div className="text-secondary small">No choices yet.</div>}
                <div className="d-flex flex-column gap-2">
                    {choicesList.map((c, idx) => {
                        return <div key={"choice-" + idx} className="choice-row animate-in">
                            <Form.Control className="mono choice-id" disabled={disabled} type="text" placeholder="choice_id" value={c.getId()}
                                onChange={(e) => edit((q) => q.getChoicesList()[idx].setId(e.target.value))} />
                            <Form.Control disabled={disabled} type="text" placeholder="Choice label" value={c.getMessage()}
                                onChange={(e) => edit((q) => q.getChoicesList()[idx].setMessage(e.target.value))} />
                            {hasEditAccess && <button className="icon-btn danger" title="Remove choice" onClick={() => edit((q) => {
                                const edited = [...q.getChoicesList()]
                                edited.splice(idx, 1)
                                q.setChoicesList(edited)
                            })}><Trash3 size={13} /></button>}
                        </div>
                    })}
                </div>
            </Section>

            <Section title={`Response actions · ${actionsList.length}`} action={hasEditAccess &&
                <button className="btn btn-ghost btn-sm" onClick={() => edit((q) => q.setActionsList([...q.getActionsList(), new ResponseAction()]))}>
                    <PlusLg /> Add action
                </button>}>
                {actionsList.length === 0 && <div className="text-secondary small">No response actions. Add one to recommend a fix when the homeowner picks a certain answer.</div>}
                <div className="d-flex flex-column gap-3">
                    {actionsList.map((action, idx) => {
                        const priority = PRIORITY_OPTIONS.find((p) => p.value === action.getPriority())
                        return <div key={"action-" + idx} className={"action-card animate-in tone-" + (priority?.tone || "info")}>
                            <div className="action-card-header">
                                <LightningCharge className="action-icon" />
                                <span className="text-secondary small">When answered</span>
                                <Form.Select size="sm" className="w-auto" disabled={disabled} value={action.getChoiceId()} onChange={(e) => editAction(idx, (a) => {
                                    a.setChoiceId(e.target.value)
                                    a.setId(e.target.value)
                                })}>
                                    <option key="" value="">Unselected</option>
                                    {choicesList.map((c, i) => {
                                        return <option key={c.getId() + i} value={c.getId()}>{c.getMessage()}</option>
                                    })}
                                </Form.Select>
                                {hasEditAccess && <button className="icon-btn danger ms-auto" title="Delete action" onClick={() => edit((q) => {
                                    const edited = [...q.getActionsList()]
                                    edited.splice(idx, 1)
                                    q.setActionsList(edited)
                                })}><Trash3 size={13} /></button>}
                            </div>
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="field-label">Priority</label>
                                    <div className="segmented">
                                        {PRIORITY_OPTIONS.map((p) => (
                                            <button key={p.value} type="button" disabled={disabled}
                                                className={"segment tone-" + p.tone + (action.getPriority() === p.value ? " active" : "")}
                                                onClick={() => editAction(idx, (a) => a.setPriority(p.value))}>{p.label}</button>
                                        ))}
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <label className="field-label">Difficulty</label>
                                    <Form.Select disabled={disabled} value={action.getDifficulty()}
                                        onChange={(e) => editAction(idx, (a) => a.setDifficulty(Number(e.target.value) as Difficulty))}>
                                        {DIFFICULTY_OPTIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                                    </Form.Select>
                                </div>
                                <div className="col-12">
                                    <label className="field-label">Recommendation title</label>
                                    <Form.Control disabled={disabled} type="text" placeholder="e.g. Replace the wax ring" value={action.getTitle()}
                                        onChange={(e) => editAction(idx, (a) => a.setTitle(e.target.value))} />
                                </div>
                                <div className="col-12">
                                    <label className="field-label">Recommendation</label>
                                    <Form.Control disabled={disabled} as="textarea" rows={4} placeholder="What should the homeowner do, and why?" value={action.getMessage()}
                                        onChange={(e) => editAction(idx, (a) => a.setMessage(e.target.value))} />
                                </div>
                                <div className="col-12">
                                    <label className="field-label">Photo prompt</label>
                                    <Form.Control disabled={disabled} type="text" placeholder="Optional: ask for a photo" value={action.getPhotoPrompt()}
                                        onChange={(e) => editAction(idx, (a) => a.setPhotoPrompt(e.target.value))} />
                                </div>
                                <div className="col-12">
                                    <label className="field-label">Estimated cost (USD)</label>
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="input-prefix"><span>$</span>
                                            <Form.Control disabled={disabled} type="number" placeholder="Min" value={action.getEstimatedCostMin() || ""}
                                                onChange={(e) => editAction(idx, (a) => a.setEstimatedCostMin(parseFloat(e.target.value) || 0))} />
                                        </div>
                                        <span className="text-secondary">–</span>
                                        <div className="input-prefix"><span>$</span>
                                            <Form.Control disabled={disabled} type="number" placeholder="Max" value={action.getEstimatedCostMax() || ""}
                                                onChange={(e) => editAction(idx, (a) => a.setEstimatedCostMax(parseFloat(e.target.value) || 0))} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    })}
                </div>
            </Section>

            <Section title="Visibility">
                <Form.Check id="q-conditional" disabled={disabled} type="switch" label="Only show this question for certain answers to another question" checked={isConditionalQuestion} onChange={(e) => {
                    setIsConditionalQuestion(e.target.checked)
                    if (!e.target.checked) {
                        edit((q) => q.clearDependentQuestionChoiceIdsList().clearDependentQuestionId())
                    }
                }} />
                {isConditionalQuestion && <div className="conditional-body animate-in">
                    <label className="field-label" htmlFor="q-depends">Only show if</label>
                    <Form.Select id="q-depends" disabled={disabled} value={question.getDependentQuestionId() || "-1"} onChange={(e) => {
                        if (e.target.value !== "-1") {
                            edit((q) => q.setDependentQuestionId(e.target.value))
                        }
                    }}>
                        <option value="-1">Choose a question</option>
                        {allQuestions.filter((q) => q.getId() !== question.getId()).map((q, i) => {
                            return <option key={q.getId() + i} value={q.getId()}>{q.getPrompt()}</option>
                        })}
                    </Form.Select>
                    {dependentQuestionMissing && <div className="small text-warning mt-2">
                        No single question in this group has the ID “{question.getDependentQuestionId()}”.
                    </div>}
                    {allDependentQuestionChoices.length > 0 && <>
                        <label className="field-label mt-3">has an answer of</label>
                        <div className="d-flex flex-wrap gap-2">
                            {allDependentQuestionChoices.map((c) => {
                                const checked = selectedDependentChoicesIdSet.has(c.getId())
                                return <button key={c.getId()} type="button" disabled={disabled}
                                    className={"toggle-chip" + (checked ? " active" : "")}
                                    onClick={() => {
                                        if (checked) {
                                            selectedDependentChoicesIdSet.delete(c.getId())
                                        } else {
                                            selectedDependentChoicesIdSet.add(c.getId())
                                        }
                                        edit((q) => q.setDependentQuestionChoiceIdsList(Array.from(selectedDependentChoicesIdSet)))
                                    }}>{c.getMessage()}</button>
                            })}
                        </div>
                    </>}
                </div>}
            </Section>
        </div>
    </div>
});

export default QuestionEditorPane
